import { useEffect, useMemo, useState } from "react";
import { adminApi, formatApiErrorDetail } from "./api";

const LEADERSHIP_NOTE =
  "Pending Ken's final approval — safe to publish for review, replace before public launch.";

const ASSISTANT_FAQ = Array.from({ length: 8 }, (_, i) => [
  { key: `assistant.faq.${i}.question`, label: `FAQ #${i + 1} — question` },
  { key: `assistant.faq.${i}.answer`, label: `FAQ #${i + 1} — answer`, multi: true },
]).flat();

const GROUPS = [
  {
    label: "Company",
    keys: [
      { key: "company.intro", label: "Intro", multi: true },
      { key: "company.vision", label: "Vision", multi: true },
      { key: "company.mission", label: "Mission", multi: true },
    ],
  },
  {
    label: "Leadership",
    note: LEADERSHIP_NOTE + " Portrait pending — swap point marked in Leadership.jsx.",
    keys: [
      { key: "leadership.message", label: "Full message", multi: true, note: LEADERSHIP_NOTE },
      { key: "leadership.short_quote", label: "Short quote", multi: true, note: LEADERSHIP_NOTE },
    ],
  },
  {
    label: "Careers",
    keys: [{ key: "careers.body", label: "Body", multi: true }],
  },
  {
    label: "Contact — Ken (legacy, no longer surfaced)",
    note: "These fields are legacy. The public site now shows a single unnamed company telephone (defined in /app/frontend/src/data/contact.js). Values saved here have no visible effect. Leave blank.",
    keys: [
      { key: "contact.ken.title", label: "Title (still shown on Leadership page)" },
      { key: "contact.ken.uae_phone", label: "UAE phone — LEGACY, leave blank" },
      { key: "contact.ken.canada_phone", label: "Canada phone — LEGACY, leave blank" },
    ],
  },
  {
    label: "Contact — Fabio (legacy, no longer surfaced)",
    note: "These fields are legacy. Fabio's Canada sales line is defined in /app/frontend/src/data/contact.js. Leave the phone fields blank.",
    keys: [
      { key: "contact.fabio.title", label: "Title — should be 'Sales'" },
      { key: "contact.fabio.uae_phone", label: "UAE phone — LEGACY, leave blank" },
      { key: "contact.fabio.canada_phone", label: "Canada phone — LEGACY, leave blank" },
    ],
  },
  {
    label: "Contact — General",
    keys: [{ key: "contact.sales_email", label: "Business email" }],
  },
  {
    label: "Assistant Knowledge",
    note: "Edit here, then click Publish assistant knowledge to compile the corpus. New answers effective on the next Ask.",
    isAssistant: true,
    keys: [
      { key: "assistant.corpus.company_intro", label: "Company intro", multi: true },
      { key: "assistant.corpus.divisions_intro", label: "Divisions intro", multi: true },
      { key: "assistant.corpus.metals_intro", label: "Precious metals intro", multi: true },
      { key: "assistant.corpus.relationships_intro", label: "Relationships intro", multi: true },
      { key: "assistant.corpus.leadership_intro", label: "Leadership intro", multi: true },
      { key: "assistant.corpus.careers_intro", label: "Careers intro", multi: true },
      { key: "assistant.corpus.contact_intro", label: "Contact intro", multi: true },
      ...ASSISTANT_FAQ,
    ],
  },
];

function publicUrlFor(key) {
  if (key.startsWith("company.")) return "/company";
  if (key.startsWith("leadership.")) return "/leadership";
  if (key.startsWith("careers.")) return "/careers";
  if (key.startsWith("contact.")) return "/contact";
  if (key.startsWith("assistant.")) return "/?assistant=open";
  return "/";
}

export default function ContentEditor() {
  const [values, setValues] = useState({});
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState("");
  const [err, setErr] = useState("");
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishMsg, setPublishMsg] = useState("");
  const [history, setHistory] = useState({}); // key -> [{version_id,value,replaced_at}]
  const [openHistory, setOpenHistory] = useState(null);

  useEffect(() => {
    adminApi.getContent().then((r) => {
      setValues(r.data.values);
      setDraft(r.data.values);
    });
  }, []);

  const dirty = useMemo(() => {
    return Object.entries(draft)
      .filter(([k, v]) => values[k] !== v)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
  }, [draft, values]);

  const dirtyCount = Object.keys(dirty).length;

  const save = async () => {
    if (dirtyCount === 0) return;
    setSaving(true); setErr(""); setSaved("");
    try {
      const { data } = await adminApi.putContent(dirty);
      setValues(data.values); setDraft(data.values);
      setSaved(`Saved ${dirtyCount} field${dirtyCount === 1 ? "" : "s"}.`);
      setTimeout(() => setSaved(""), 3000);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Save failed.");
    } finally { setSaving(false); }
  };

  const publish = async () => {
    setPublishBusy(true); setPublishMsg("");
    try {
      const { data } = await adminApi.publishAssistant();
      setPublishMsg(`Assistant knowledge published. New answers effective immediately. (v${data.corpus_version}, ${data.bytes_written} bytes)`);
      setTimeout(() => setPublishMsg(""), 4000);
    } catch (e) {
      setPublishMsg("Publish failed: " + (e.response?.data?.detail || e.message));
    } finally { setPublishBusy(false); }
  };

  const loadHistory = async (key) => {
    if (openHistory === key) { setOpenHistory(null); return; }
    setOpenHistory(key);
    const { data } = await adminApi.historyKey(key);
    setHistory((h) => ({ ...h, [key]: data.history }));
  };

  const restore = async (key, version_id) => {
    const { data } = await adminApi.restoreKey(key, version_id);
    setValues((v) => ({ ...v, [key]: data.value }));
    setDraft((v) => ({ ...v, [key]: data.value }));
    setOpenHistory(null);
    setSaved("Restored.");
    setTimeout(() => setSaved(""), 2000);
  };

  return (
    <div className="ga-page" data-testid="content-editor">
      <div className="ga-eyebrow">Content</div>
      <h1 className="ga-title">Page copy</h1>
      <p className="ga-mute" style={{ maxWidth: "60ch" }}>
        Edits go live immediately on the public site. Empty a field to fall back to the built-in default.
      </p>

      <div className="ga-content-groups">
        {GROUPS.map((g) => (
          <section key={g.label} className="ga-group" data-testid={`group-${slug(g.label)}`}>
            <h2 className="ga-group__title">{g.label}</h2>
            {g.note && (
              <div className="ga-group__note" data-testid={`group-note-${slug(g.label)}`}>{g.note}</div>
            )}
            {g.keys.map(({ key, label, multi, note }) => {
              const isSaved = values[key] === draft[key];
              return (
                <label key={key} className="ga-field ga-field--wide" data-testid={`field-${key}`}>
                  <span>
                    {label} <code className="ga-code">{key}</code>
                  </span>
                  {multi ? (
                    <textarea rows={4} value={draft[key] ?? ""} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} data-testid={`input-${key}`} />
                  ) : (
                    <input type="text" value={draft[key] ?? ""} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} data-testid={`input-${key}`} />
                  )}
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 6, fontSize: 12 }}>
                    {isSaved && (
                      <a href={publicUrlFor(key)} target="_blank" rel="noopener noreferrer" data-testid={`open-public-${key}`} style={{ color: "var(--gp-gold)" }}>
                        Open public page →
                      </a>
                    )}
                    <button type="button" onClick={() => loadHistory(key)} data-testid={`restore-${key}`} className="ga-link-btn" style={{ background: "transparent", border: 0, color: "rgba(255,255,255,0.6)", cursor: "pointer", padding: 0, fontSize: 12 }}>
                      Restore previous
                    </button>
                  </div>
                  {openHistory === key && (
                    <div style={{ marginTop: 8, padding: 10, border: "1px solid var(--gp-line-dark)" }} data-testid={`history-${key}`}>
                      {(history[key] || []).length === 0 && (
                        <div className="ga-mute" style={{ fontSize: 12 }}>No previous versions yet.</div>
                      )}
                      {(history[key] || []).map((h) => (
                        <div key={h.version_id} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "6px 0", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                          <div style={{ flex: 1, fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
                            <div className="ga-mute" style={{ fontSize: 11 }}>{h.replaced_at}</div>
                            <div style={{ whiteSpace: "pre-wrap" }}>{h.value.slice(0, 200)}{h.value.length > 200 ? "…" : ""}</div>
                          </div>
                          <button type="button" onClick={() => restore(key, h.version_id)} className="ga-btn ga-btn--ghost" style={{ fontSize: 11 }}>Restore this</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {note && <em className="ga-field__note" data-testid={`field-note-${key}`}>{note}</em>}
                </label>
              );
            })}
            {g.isAssistant && (
              <div style={{ marginTop: 12, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <button type="button" className="ga-btn" onClick={publish} disabled={publishBusy} data-testid="publish-assistant">
                  {publishBusy ? "Publishing…" : "Publish assistant knowledge"}
                </button>
                {publishMsg && <span className="ga-saved" data-testid="publish-msg">{publishMsg}</span>}
              </div>
            )}
          </section>
        ))}
      </div>

      <div className="ga-savebar">
        <div>
          {err && <span className="ga-error">{err}</span>}
          {saved && <span className="ga-saved" data-testid="save-confirm">{saved}</span>}
          {!err && !saved && (
            <span className="ga-mute">
              {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}.
            </span>
          )}
        </div>
        <button type="button" className="ga-btn" onClick={save} disabled={saving || dirtyCount === 0} data-testid="save-content">
          {saving ? "Saving…" : `Save${dirtyCount ? ` (${dirtyCount})` : ""}`}
        </button>
      </div>
    </div>
  );
}

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
